import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Brand from "@/components/Brand";
import { IdentificationCard, WarningOctagon, CheckCircle, CurrencyDollar, ArrowLeft, LockKey } from "@phosphor-icons/react";

// Public owner portal: enter plate + national ID → verify against
// plate.owner_hash → list violations → mock-pay open ones.
export default function OwnerPortal() {
    const { t } = useI18n();
    const [plate, setPlate] = useState("");
    const [nid, setNid] = useState("");
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [payingId, setPayingId] = useState(null);

    const lookup = async (e) => {
        e?.preventDefault?.();
        setBusy(true);
        try {
            const { data } = await api.post("/public/owner-portal/lookup", {
                plate_number: plate.trim().toUpperCase(),
                national_id: nid.trim(),
            });
            setData(data);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("error"));
            setData(null);
        }
        setBusy(false);
    };

    const pay = async (vid) => {
        setPayingId(vid);
        try {
            const { data: res } = await api.post(`/public/violations/${vid}/pay`, {
                plate_number: plate.trim().toUpperCase(),
                national_id: nid.trim(),
            });
            toast.success(t("ownerPortalPaid"));
            setData((d) => d ? {
                ...d,
                violations: d.violations.map((v) => v.id === vid ? { ...v, status: "paid" } : v),
            } : d);
        } catch (err) {
            toast.error(err?.response?.data?.detail || t("error"));
        }
        setPayingId(null);
    };

    const open = (data?.violations || []).filter((v) => v.status === "open");
    const closed = (data?.violations || []).filter((v) => v.status !== "open");
    const openTotal = open.reduce((s, v) => s + (v.amount || 0), 0);

    return (
        <div className="min-h-screen bg-background flex flex-col">
            <header className="px-6 py-4 border-b border-border flex items-center justify-between">
                <Brand />
                <Link to="/" className="text-xs uppercase tracking-widest text-muted-foreground hover:text-foreground flex items-center gap-1"><ArrowLeft size={12} weight="bold" />{t("home") || "Home"}</Link>
            </header>
            <div className="flex-1 max-w-3xl mx-auto w-full p-6 space-y-6" data-testid="owner-portal-page">
                <div>
                    <div className="text-[11px] tracking-[0.35em] uppercase text-primary">· PUBLIC · OWNER PORTAL</div>
                    <h1 className="text-3xl font-bold tracking-tight mt-1">{t("ownerPortalTitle")}</h1>
                    <p className="text-sm text-muted-foreground mt-1">{t("ownerPortalHint")}</p>
                </div>

                <Card className="p-5 rounded-sm border-border">
                    <form onSubmit={lookup} className="grid sm:grid-cols-3 gap-3 items-end" data-testid="owner-portal-form">
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("plateNumber")}</Label>
                            <Input value={plate} onChange={(e) => setPlate(e.target.value.toUpperCase())} className="mono h-11 mt-1" data-testid="op-plate" required />
                        </div>
                        <div>
                            <Label className="text-[10px] tracking-widest uppercase font-bold text-muted-foreground"><LockKey size={11} className="inline me-1" />{t("nationalId")}</Label>
                            <Input value={nid} onChange={(e) => setNid(e.target.value)} className="mono h-11 mt-1" data-testid="op-nid" required />
                        </div>
                        <Button type="submit" disabled={busy || !plate.trim() || !nid.trim()} className="h-11 rounded-sm bg-primary hover:bg-primary/90" data-testid="op-submit">
                            {busy ? t("loading") : t("ownerPortalLookup")}
                        </Button>
                    </form>
                    <div className="text-[11px] text-muted-foreground mt-2">{t("ownerPortalPrivacy")}</div>
                </Card>

                {data && (
                    <>
                        <Card className="p-5 rounded-sm border-border" data-testid="op-summary">
                            <div className="flex items-center gap-3 flex-wrap">
                                <IdentificationCard size={24} weight="duotone" className="text-primary" />
                                <div>
                                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("plateNumber")}</div>
                                    <div className="mono text-xl font-black">{data.plate.plate_number}</div>
                                </div>
                                <div className="ms-auto text-end">
                                    <div className="text-[10px] tracking-widest uppercase text-muted-foreground">{t("ownerPortalOpenTotal")}</div>
                                    <div className="mono text-xl font-black text-primary" data-testid="op-open-total">{openTotal.toFixed(0)}</div>
                                </div>
                            </div>
                        </Card>

                        {open.length > 0 && (
                            <Card className="rounded-sm border-border overflow-hidden">
                                <div className="px-4 py-2 bg-primary/10 text-[10px] tracking-widest uppercase font-bold text-primary flex items-center gap-1"><WarningOctagon size={12} weight="fill" />{t("ownerPortalOpen")} · {open.length}</div>
                                <ul className="divide-y divide-border">
                                    {open.map((v) => (
                                        <li key={v.id} className="p-4 flex items-start justify-between gap-3 flex-wrap" data-testid={`op-open-${v.id}`}>
                                            <div>
                                                <div className="font-medium">{v.type_label}</div>
                                                <div className="text-[11px] text-muted-foreground mono">{new Date(v.issued_at).toLocaleString()} · {v.location || "—"}</div>
                                                {v.notes && <div className="text-xs text-muted-foreground mt-1">{v.notes}</div>}
                                            </div>
                                            <div className="text-end shrink-0">
                                                <div className="mono font-bold">{v.amount} {v.currency}</div>
                                                <Button size="sm" onClick={() => pay(v.id)} disabled={payingId === v.id} className="mt-1 rounded-sm bg-primary hover:bg-primary/90" data-testid={`op-pay-${v.id}`}>
                                                    <CurrencyDollar size={12} weight="bold" className="me-1" />
                                                    {payingId === v.id ? t("loading") : t("ownerPortalPay")}
                                                </Button>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}
                        {open.length === 0 && (
                            <div className="flex items-center gap-2 text-green-700 text-sm bg-green-50 border border-green-200 p-4 rounded-sm" data-testid="op-none-open">
                                <CheckCircle size={18} weight="fill" />{t("ownerPortalNoOpen")}
                            </div>
                        )}
                        {closed.length > 0 && (
                            <Card className="rounded-sm border-border overflow-hidden">
                                <div className="px-4 py-2 bg-secondary text-[10px] tracking-widest uppercase font-bold text-muted-foreground">{t("ownerPortalHistory")} · {closed.length}</div>
                                <ul className="divide-y divide-border text-sm">
                                    {closed.map((v) => (
                                        <li key={v.id} className="p-3 flex justify-between gap-3">
                                            <div>
                                                <div>{v.type_label}</div>
                                                <div className="text-[11px] text-muted-foreground mono">{new Date(v.issued_at).toLocaleString()}</div>
                                            </div>
                                            <div className="text-end">
                                                <div className="mono">{v.amount} {v.currency}</div>
                                                <div className={`text-[10px] uppercase tracking-widest ${v.status === "paid" ? "text-green-700" : "text-muted-foreground"}`}>{v.status}</div>
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </Card>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
